import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Upload, FileText, CheckCircle2, Loader2, X, AlertCircle } from "lucide-react";
import { useFileUpload } from "@/hooks/useFileUpload";
import { useMatters, useMatterStages } from "@/hooks/use-supabase-queries";
import { DOCUMENT_CATEGORIES, DOCUMENT_VISIBILITY } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMatterId?: string;
  defaultStageId?: string;
  defaultCategory?: string;
  defaultVisibility?: string;
  dialogTitle?: string;
  dialogDescription?: string;
  maxSizeMB?: number;
}

export function DocumentUploadDialog({
  open,
  onOpenChange,
  defaultMatterId,
  defaultStageId,
  defaultCategory,
  defaultVisibility = "lawyer-only",
  dialogTitle = "Upload Document",
  dialogDescription,
  maxSizeMB = 10,
}: DocumentUploadDialogProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { upload, isUploading, progress, error: uploadError, validate } = useFileUpload(maxSizeMB);
  const { data: matters } = useMatters();
  const [matterId, setMatterId] = useState(defaultMatterId || "");
  const { data: stages } = useMatterStages(matterId || undefined);

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState(defaultCategory || "");
  const [stageId, setStageId] = useState(defaultStageId || "");
  const [visibility, setVisibility] = useState(defaultVisibility);
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Determine if user is a lawyer (only lawyers get visibility control)
  const userRole = user ? 'lawyer' : 'client'; // simplified; actual role from profile

  const handleFileSelect = useCallback((f: File) => {
    const err = validate(f);
    if (err) {
      setFileError(err);
      setFile(null);
      return;
    }
    setFileError(null);
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^/.]+$/, ""));
  }, [name, validate]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  }, [handleFileSelect]);

  const handleSubmit = async () => {
    if (!file || !name.trim()) return;
    try {
      await upload({
        file,
        name: name.trim(),
        category: category || undefined,
        matter_id: matterId || undefined,
        stage_id: stageId || undefined,
        visibility,
      });
      setSuccess(true);
      toast.success("Document uploaded successfully");
    } catch {
      // error already in uploadError
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      onOpenChange(false);
      // Reset after animation
      setTimeout(() => {
        setFile(null);
        setName("");
        setCategory(defaultCategory || "");
        setStageId(defaultStageId || "");
        setMatterId(defaultMatterId || "");
        setVisibility(defaultVisibility);
        setFileError(null);
        setSuccess(false);
      }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>
            {dialogDescription || `Upload a document to the system. Accepted formats: PDF, JPG, PNG (max ${maxSizeMB}MB).`}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
            <h3 className="font-semibold text-foreground">Upload Complete!</h3>
            <p className="text-sm text-muted-foreground">"{name}" has been uploaded successfully.</p>
            <Button onClick={handleClose}>Done</Button>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-2">
              {/* Drop zone */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileSelect(f);
                  }}
                />
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                    isDragging
                      ? 'border-primary bg-primary/5'
                      : fileError
                      ? 'border-destructive bg-destructive/5'
                      : file
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  {file ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="h-8 w-8 text-primary" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-foreground">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => { e.stopPropagation(); setFile(null); setFileError(null); }}
                        aria-label="Remove file"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm font-medium text-foreground">
                        {isDragging ? 'Drop file here' : 'Drag & drop or click to browse'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG up to {maxSizeMB}MB</p>
                    </>
                  )}
                </div>
                {fileError && (
                  <div className="flex items-center gap-1 mt-2 text-destructive text-sm">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span>{fileError}</span>
                  </div>
                )}
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload file
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => navigate("/camera-capture?facing=environment")}>
                    <Camera className="mr-2 h-4 w-4" />
                    Back camera
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => navigate("/camera-capture?facing=user")}>
                    <Camera className="mr-2 h-4 w-4" />
                    Front camera
                  </Button>
                </div>
              </div>

              <div>
                <Label>Document Name *</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Certificate of Occupancy" />
              </div>

              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(DOCUMENT_CATEGORIES).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!defaultMatterId && matters && matters.length > 0 && (
                <div>
                  <Label>Link to Matter</Label>
                  <Select value={matterId} onValueChange={setMatterId}>
                    <SelectTrigger><SelectValue placeholder="Select matter (optional)" /></SelectTrigger>
                    <SelectContent>
                      {matters.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.matter_number} — {m.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {!defaultStageId && stages && stages.length > 0 && (
                <div>
                  <Label>Stage</Label>
                  <Select value={stageId} onValueChange={setStageId}>
                    <SelectTrigger><SelectValue placeholder="Select stage (optional)" /></SelectTrigger>
                    <SelectContent>
                      {stages.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.stage_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Visibility</Label>
                <Select value={visibility} onValueChange={setVisibility}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(DOCUMENT_VISIBILITY).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Upload progress */}
              {isUploading && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Uploading...</span>
                    <span className="text-foreground font-medium">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}

              {uploadError && (
                <div className="flex items-center gap-2 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>{uploadError}</span>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isUploading}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={!file || !name.trim() || isUploading}>
                {isUploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : 'Upload'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
